import type { ComputedRef } from 'vue';
import { computed, getCurrentInstance } from 'vue';

//* UTILITIES *//
import { getVm } from '@/helpers/vm-helper';
import { mapGetters, mapState, mapActions } from 'vuex';

type Prettify<T> = { [K in keyof T]: T[K] } & {};

interface StoreProperties<StateKey extends string, GetterKey extends string, ActionKey extends string> {
	state: ReadonlyArray<StateKey>;
	getters: ReadonlyArray<GetterKey>;
	actions: ReadonlyArray<ActionKey>;
}

/*
For the State type, we use computed instead of ref to avoid losing reactivity.
This can happen with how Vuex and the app sometimes updates state (changing country for example.)
*/
type State<StateKey extends string> = Record<StateKey, ComputedRef<any>>;
type Getters<GetterKey extends string> = Record<GetterKey, ComputedRef<any>>;
type Actions<ActionKey extends string> = Record<ActionKey, (...args: any) => Promise<any>>;

function useStore<StateKey extends string, GetterKey extends string, ActionKey extends string>(
	namespace: string,
	{ state, getters, actions }: StoreProperties<StateKey, GetterKey, ActionKey>
) {
	const { proxy: instance } = getCurrentInstance() ?? { proxy: getVm() };

	const mappedState = mapState(namespace, state as StateKey[]);
	const mappedGetters = mapGetters(namespace, getters as GetterKey[]);
	const mappedActions = mapActions(namespace, actions as ActionKey[]);

	return Object.assign(
		Object.fromEntries(state.map(key => [key, computed(() => mappedState[key].call(instance))])),
		Object.fromEntries(getters.map(key => [key, computed(() => mappedGetters[key].call(instance))])),
		Object.fromEntries(actions.map(key => [key, (...args: any) => mappedActions[key].apply(instance, args)]))
	) as Prettify<State<StateKey> & Getters<GetterKey> & Actions<ActionKey>>;
}

//* STORES *//
// TODO: autocomplete possible values for state, getters, and actions based on given namespace and Vuex store
export const useLanguageStore = () =>
	useStore('language', {
		state: ['webLocale', 'locales'],
		getters: ['language', 'country', 'isTTBLanguage'],
		actions: ['setWebLocale'],
	});

export const useUserStore = () =>
	useStore('user', {
		state: ['isPremium'],
		getters: ['buyboxPresentationType', 'isLoggedIn'],
		actions: ['setBuyboxPresentationType'],
	});

export const useRoutingStore = () =>
	useStore('routing', {
		state: [],
		getters: ['urlMetadata'],
		actions: [],
	});

export const useExperimentStore = () =>
	useStore('experiment', {
		state: [],
		getters: ['activeVariantsWithControlGroup', 'activeVariants'],
		actions: [],
	});
